'use client';

import { Button } from '@ui/components/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@ui/components/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@ui/components/dialog';
import { Input } from '@ui/components/input';
import { Label } from '@ui/components/label';
import { AppCard, PageLayout } from '@ui/components/layouts';
import { SimpleSelect } from '@ui/components/select';
import { text as sharedText } from '@shared/text';
import { text } from '@modules/plants/text';
import type { PlantsProps } from '@modules/plants/containers/plants_container';
import { PlantAvatarWeb } from '@modules/plants/views/plant_avatar_web';
import { PageContentLoading } from '@/src/components/PageContentLoading';
import { FaIcon } from '@micks-yard/icons/FaIcon';
import { faPenToSquare } from '@micks-yard/icons/solid/faPenToSquare';
import { faTrash } from '@micks-yard/icons/solid/faTrash';
import type { Id } from 'convex/_generated/dataModel';

function PlantFormFields({
  idPrefix,
  draftName,
  draftEmoji,
  draftCategory,
  categories,
  isSaving,
  onDraftNameChange,
  onDraftEmojiChange,
  onDraftCategoryChange,
}: Pick<
  PlantsProps,
  | 'draftName'
  | 'draftEmoji'
  | 'draftCategory'
  | 'categories'
  | 'isSaving'
  | 'onDraftNameChange'
  | 'onDraftEmojiChange'
  | 'onDraftCategoryChange'
> & { idPrefix: string }) {
  const categoryOptions = [
    ...categories.map((category) => ({ value: category, label: category })),
    ...(categories.includes('Other') ? [] : [{ value: 'Other', label: 'Other' }]),
  ];

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <Label htmlFor={`${idPrefix}-name`}>{text.plantNameLabel}</Label>
        <Input
          id={`${idPrefix}-name`}
          value={draftName}
          onChange={(event) => onDraftNameChange(event.target.value)}
          placeholder={text.plantNamePlaceholder}
          autoFocus
          disabled={isSaving}
        />
      </div>
      <div className="flex flex-col gap-2">
        <Label htmlFor={`${idPrefix}-emoji`}>{text.plantEmojiLabel}</Label>
        <Input
          id={`${idPrefix}-emoji`}
          value={draftEmoji}
          onChange={(event) => onDraftEmojiChange(event.target.value)}
          placeholder={text.plantEmojiPlaceholder}
          disabled={isSaving}
        />
      </div>
      <div className="flex flex-col gap-2">
        <SimpleSelect
          id={`${idPrefix}-category`}
          value={draftCategory}
          onValueChange={onDraftCategoryChange}
          options={categoryOptions}
          disabled={isSaving}
          label={text.plantCategoryLabel}
        />
      </div>
    </div>
  );
}

export function Plants({
  isLoading,
  isSaving,
  visiblePlants,
  categories,
  searchQuery,
  selectedCategory,
  onSearchQueryChange,
  onSelectedCategoryChange,
  onStartCreating,
  onStartEditing,
  onConfirmDelete,
  canEditPlant,
  createDialogOpen,
  editDialogOpen,
  deleteDialogOpen,
  draftName,
  draftEmoji,
  draftCategory,
  onDraftNameChange,
  onDraftEmojiChange,
  onDraftCategoryChange,
  onCreateSubmit,
  onCreateCancel,
  onEditSubmit,
  onEditCancel,
  plantToDeleteName,
  onDeleteConfirm,
  onDeleteCancel,
}: PlantsProps) {
  if (isLoading) {
    return <PageContentLoading />;
  }

  const categoryFilterOptions = [
    { value: '__all__', label: text.allCategories },
    ...categories.map((category) => ({ value: category, label: category })),
  ];

  return (
    <>
      <PageLayout title={text.title} maxWidth={896}>
        <AppCard className="p-4">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <h3 className="text-lg font-semibold">{text.sectionTitle}</h3>
            <Button size="sm" onClick={onStartCreating} disabled={isSaving}>
              {text.addPlant}
            </Button>
          </div>

          <div className="mb-4 flex flex-col gap-3">
            <Input
              value={searchQuery}
              onChange={(event) => onSearchQueryChange(event.target.value)}
              placeholder={text.searchPlaceholder}
              disabled={isSaving}
            />
            <SimpleSelect
              value={selectedCategory ?? '__all__'}
              onValueChange={(value) =>
                onSelectedCategoryChange(value === '__all__' ? null : value)
              }
              options={categoryFilterOptions}
              disabled={isSaving}
            />
          </div>

          {visiblePlants.length === 0 ? (
            <div className="flex flex-col items-center gap-2 py-8">
              <p className="text-muted-foreground">{text.emptyTitle}</p>
              <p className="text-muted-foreground text-sm">{text.emptyHint}</p>
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              <p className="text-muted-foreground text-sm">
                {text.plantsTotal(visiblePlants.length)}
              </p>
              <div className="flex flex-wrap gap-3">
                {visiblePlants.map((plant) => (
                  <AppCard
                    key={plant.id}
                    className="hover:bg-muted/50 w-full p-3 md:w-[48%] lg:w-[31%]">
                    <div className="flex items-center gap-3">
                      <PlantAvatarWeb
                        name={plant.name}
                        emoji={plant.emoji}
                        imagePath={plant.imagePath}
                        size={32}
                      />
                      <div className="flex min-w-0 flex-1 flex-col">
                        <div className="flex items-center gap-2">
                          <span className="truncate font-semibold">{plant.name}</span>
                          {plant.isUserOwned ? (
                            <span className="rounded-md bg-green-100 px-2 py-0.5 text-xs text-green-700">
                              {text.yourPlantBadge}
                            </span>
                          ) : null}
                        </div>
                        <p className="text-muted-foreground text-sm">{plant.category}</p>
                      </div>
                      {canEditPlant(plant) ? (
                        <div className="flex gap-1">
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon-sm"
                            onClick={() => onStartEditing(plant)}
                            disabled={isSaving}
                            aria-label={text.editPlant}>
                            <FaIcon icon={faPenToSquare} className="size-4" />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon-sm"
                            onClick={() => onConfirmDelete(plant.id as Id<'userPlants'>)}
                            disabled={isSaving}
                            aria-label={text.deletePlantAction}>
                            <FaIcon icon={faTrash} className="size-4" />
                          </Button>
                        </div>
                      ) : null}
                    </div>
                  </AppCard>
                ))}
              </div>
            </div>
          )}
        </AppCard>
      </PageLayout>

      <Dialog open={createDialogOpen} onOpenChange={(open) => !open && onCreateCancel()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{text.createDialogTitle}</DialogTitle>
            <DialogDescription>{text.createDialogDescription}</DialogDescription>
          </DialogHeader>
          <form onSubmit={onCreateSubmit}>
            <PlantFormFields
              idPrefix="create-plant"
              draftName={draftName}
              draftEmoji={draftEmoji}
              draftCategory={draftCategory}
              categories={categories}
              isSaving={isSaving}
              onDraftNameChange={onDraftNameChange}
              onDraftEmojiChange={onDraftEmojiChange}
              onDraftCategoryChange={onDraftCategoryChange}
            />
            <DialogFooter>
              <Button type="button" variant="outline" onClick={onCreateCancel} disabled={isSaving}>
                {sharedText.cancel}
              </Button>
              <Button type="submit" disabled={!draftName.trim() || isSaving}>
                {text.savePlant}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={editDialogOpen} onOpenChange={(open) => !open && onEditCancel()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{text.editDialogTitle}</DialogTitle>
            <DialogDescription>{text.editDialogDescription}</DialogDescription>
          </DialogHeader>
          <form onSubmit={onEditSubmit}>
            <PlantFormFields
              idPrefix="edit-plant"
              draftName={draftName}
              draftEmoji={draftEmoji}
              draftCategory={draftCategory}
              categories={categories}
              isSaving={isSaving}
              onDraftNameChange={onDraftNameChange}
              onDraftEmojiChange={onDraftEmojiChange}
              onDraftCategoryChange={onDraftCategoryChange}
            />
            <DialogFooter>
              <Button type="button" variant="outline" onClick={onEditCancel} disabled={isSaving}>
                {sharedText.cancel}
              </Button>
              <Button type="submit" disabled={!draftName.trim() || isSaving}>
                {text.savePlant}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteDialogOpen} onOpenChange={(open) => !open && onDeleteCancel()}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{text.deleteDialogTitle}</AlertDialogTitle>
            <AlertDialogDescription>
              {plantToDeleteName ? text.deleteDialogDescription(plantToDeleteName) : null}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isSaving}>{sharedText.cancel}</AlertDialogCancel>
            <AlertDialogAction variant="destructive" onClick={onDeleteConfirm} disabled={isSaving}>
              {text.deletePlant}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
